/**
 * PPTX Generator Utility
 *
 * Generates PowerPoint slides for Knowledge Gap and Key Decision reports
 * using pptxgenjs library. This utility is extracted from ReportWriterV2
 * component for better testability and maintainability.
 *
 * @module pptxGenerator
 */

import pptxgen from 'pptxgenjs';

/**
 * Helper function to extract tables from HTML
 * @param {string} html - HTML string from rich text editor
 * @returns {Array} - Array of table objects with rows and cells
 */
const extractTablesFromHTML = (html) => {
  if (!html) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const tables = [];

    doc.querySelectorAll('table').forEach(table => {
      const rows = [];
      table.querySelectorAll('tr').forEach(tr => {
        const cells = [];
        tr.querySelectorAll('td, th').forEach(cell => {
          cells.push({
            text: cell.textContent.trim(),
            options: {
              bold: cell.tagName === 'TH',
              fontSize: 9,
              valign: 'middle',
              align: 'left'
            }
          });
        });
        if (cells.length > 0) {
          rows.push(cells);
        }
      });

      if (rows.length > 0) {
        tables.push({
          rows,
          colCount: Math.max(...rows.map(r => r.length))
        });
      }
    });

    return tables;
  } catch (error) {
    console.error('Error extracting tables:', error);
    return [];
  }
};

/**
 * Helper function to extract images from HTML and load their dimensions
 * @param {string} html - HTML string from rich text editor
 * @returns {Promise<Array>} - Array of image objects with data and dimensions
 */
const extractImagesFromHTML = async (html) => {
  if (!html) return [];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imagePromises = [];

    doc.querySelectorAll('img').forEach(img => {
      if (img.src) {
        // Create a promise that loads the image and gets real dimensions
        const imagePromise = new Promise((resolve) => {
          const tempImg = new Image();
          tempImg.onload = () => {
            resolve({
              data: img.src,
              naturalWidth: tempImg.width,
              naturalHeight: tempImg.height,
              alt: img.alt || 'Image'
            });
          };
          tempImg.onerror = () => {
            // If image fails to load, use fallback
            resolve({
              data: img.src,
              naturalWidth: null,
              naturalHeight: null,
              alt: img.alt || 'Image'
            });
          };
          tempImg.src = img.src;
        });
        imagePromises.push(imagePromise);
      }
    });

    return await Promise.all(imagePromises);
  } catch (error) {
    console.error('Error extracting images:', error);
    return [];
  }
};

/**
 * Helper function to parse HTML and extract text with link information
 * @param {string} html - HTML string from rich text editor
 * @returns {Object} - Object with segments array and plainText string
 */
const parseHTMLWithLinks = (html) => {
  if (!html) return { segments: [], plainText: '' };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Remove tables and images first (we'll handle them separately)
    doc.querySelectorAll('table, img').forEach(el => el.remove());

    const segments = [];
    let plainText = '';

    // Recursively extract text and links
    const extractTextSegments = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text.trim()) {
          segments.push({ text, isLink: false });
          plainText += text;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        if (tagName === 'a') {
          const text = node.textContent.trim();
          const href = node.getAttribute('href');
          if (text && href) {
            segments.push({ text, isLink: true, url: href });
            plainText += text;
          }
        } else if (tagName === 'br') {
          segments.push({ text: '\n', isLink: false });
          plainText += '\n';
        } else if (['p', 'div'].includes(tagName)) {
          node.childNodes.forEach(extractTextSegments);
          segments.push({ text: '\n', isLink: false });
          plainText += '\n';
        } else if (tagName === 'li') {
          segments.push({ text: '• ', isLink: false });
          plainText += '• ';
          node.childNodes.forEach(extractTextSegments);
          segments.push({ text: '\n', isLink: false });
          plainText += '\n';
        } else {
          node.childNodes.forEach(extractTextSegments);
        }
      }
    };

    doc.body.childNodes.forEach(extractTextSegments);

    return {
      segments: segments.filter(s => s.text),
      plainText: plainText.trim()
    };
  } catch (error) {
    console.error('Error parsing HTML with links:', error);
    // Fallback to simple text extraction
    const plainText = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .trim();
    return {
      segments: [{ text: plainText, isLink: false }],
      plainText
    };
  }
};

/**
 * Generate PowerPoint blob from report data
 *
 * @param {Object} formData - Report form data
 * @param {string} formData.title - Report title
 * @param {string} formData.owner - Report owner
 * @param {string} formData.contributors - Contributors (KG only)
 * @param {string} formData.decision_maker - Decision maker (KD only)
 * @param {string} formData.learning_cycle - Learning cycle
 * @param {string} formData.project_name - Project name
 * @param {string} formData.kg_number - Knowledge Gap number (KG only)
 * @param {string} formData.kd_number - Key Decision number (KD only)
 * @param {string} formData.status - Report status
 * @param {string} formData.description - Report description
 * @param {string} formData.purpose - Report purpose
 * @param {string} formData.what_we_have_done - Actions taken
 * @param {string} formData.what_we_have_learned - Learnings
 * @param {string} formData.recommendations - Recommendations
 * @param {string} selectedReport - Report type: 'knowledge_gap' or 'key_decision'
 * @param {string} slideSize - Slide size: '16:9' or '4:3'
 * @returns {Promise<Blob>} - PowerPoint file as Blob
 */
export const generatePowerPointBlob = async (formData, selectedReport, slideSize = '16:9') => {
  const pptx = new pptxgen();

  // Set layout based on slideSize parameter
  pptx.layout = slideSize === '16:9' ? 'LAYOUT_16x9' : 'LAYOUT_4x3';

  // Color scheme based on report type
  const isKnowledgeGap = selectedReport === 'knowledge_gap';
  const headerBg = isKnowledgeGap ? 'FFFF66' : 'CCFF66';
  const borderColor = '555555';

  // Layout dimensions
  const leftMargin = 0.3;
  const rightMargin = 0.3;
  const slideWidth = 10;
  const slideHeight = slideSize === '16:9' ? 5.625 : 7.5;
  const availableWidth = slideWidth - leftMargin - rightMargin;
  const leftBoxWidth = availableWidth * 0.465;
  const rightBoxWidth = availableWidth * 0.525;
  const gap = availableWidth * 0.01;

  // Create slide
  const slide = pptx.addSlide();
  slide.background = { color: 'FFFFFF' };

  const topMargin = 0.15;
  const headerHeight = 0.85;
  const headerTextPadding = 0.08;
  const cornerRadius = 0.1;

  // Helper function to add content with tables, images, and links to a box
  const addContentToBox = async (html, x, y, w, h, titleText) => {
    // Extract tables and images (images need to be loaded to get dimensions)
    const tables = extractTablesFromHTML(html);
    const images = await extractImagesFromHTML(html);
    const parsed = parseHTMLWithLinks(html);

    // If no tables or images, use simple text box with links
    if (tables.length === 0 && images.length === 0) {
      // Build text array with title and content segments (including links)
      const textArray = [{ text: titleText, options: { bold: true } }];

      // Add text segments with hyperlinks where applicable
      parsed.segments.forEach(segment => {
        if (segment.isLink) {
          textArray.push({
            text: segment.text,
            options: {
              hyperlink: { url: segment.url, tooltip: segment.url },
              color: '0563C1',
              underline: true
            }
          });
        } else {
          textArray.push({ text: segment.text });
        }
      });

      slide.addText(textArray, {
        x, y, w, h,
        fontSize: 10,
        fontFace: 'Arial',
        fill: { color: 'FFFFFF' },
        line: { color: borderColor, width: 1 },
        shape: pptx.ShapeType.roundRect,
        rectRadius: cornerRadius,
        valign: 'top',
        margin: [0.08, 0.08, 0.08, 0.08]
      });
      return;
    }

    // Complex content: Draw border box, then add text, tables, and images
    const padding = 0.08;

    // Add border box
    slide.addShape(pptx.ShapeType.roundRect, {
      x, y, w, h,
      fill: { color: 'FFFFFF' },
      line: { color: borderColor, width: 1 },
      rectRadius: cornerRadius
    });

    let currentY = y + padding;
    const contentWidth = w - (2 * padding);
    const availableHeight = h - (2 * padding);

    // Add title
    slide.addText(titleText, {
      x: x + padding,
      y: currentY,
      w: contentWidth,
      h: 0.2,
      fontSize: 10,
      fontFace: 'Arial',
      bold: true,
      color: '000000'
    });
    currentY += 0.25;

    // Add plain text with links (if any)
    if (parsed.segments.length > 0) {
      // Calculate text content length for height estimation
      const totalTextLength = parsed.segments.reduce((sum, seg) => sum + seg.text.length, 0);

      // Estimate height based on content (more accurate than fixed height)
      // Assuming ~80 characters per line at fontSize 9 with the given width
      const charsPerLine = Math.floor(contentWidth * 11); // ~11 chars per inch at font size 9
      const estimatedLines = Math.ceil(totalTextLength / charsPerLine);
      const lineHeight = 0.15; // inches per line at fontSize 9
      const calculatedHeight = estimatedLines * lineHeight;

      // Use calculated height but cap at reasonable maximum
      const textHeight = Math.min(calculatedHeight, availableHeight * 0.4, 1.5);

      // Build text array with hyperlinks where applicable
      const textArray = [];
      parsed.segments.forEach(segment => {
        if (segment.isLink) {
          textArray.push({
            text: segment.text,
            options: {
              fontSize: 9,
              hyperlink: { url: segment.url, tooltip: segment.url },
              color: '0563C1',
              underline: true
            }
          });
        } else {
          textArray.push({
            text: segment.text,
            options: { fontSize: 9 }
          });
        }
      });

      slide.addText(textArray, {
        x: x + padding,
        y: currentY,
        w: contentWidth,
        h: textHeight,
        fontFace: 'Arial',
        color: '000000',
        valign: 'top'
      });
      currentY += textHeight + 0.15; // Increased buffer from 0.1 to 0.15
    }

    // Add tables
    tables.forEach((table, idx) => {
      const tableHeight = Math.min(
        table.rows.length * 0.25,
        y + h - currentY - padding - 0.1
      );

      if (tableHeight > 0.3) {
        slide.addTable(table.rows, {
          x: x + padding,
          y: currentY,
          w: contentWidth,
          h: tableHeight,
          fontSize: 9,
          border: { pt: 1, color: '999999' },
          fill: { color: 'F9F9F9' },
          autoPage: false,
          autoPageRepeatHeader: false
        });
        currentY += tableHeight + 0.1;
      }
    });

    // Add images with strict space checking
    images.forEach((image, idx) => {
      const remainingHeight = y + h - currentY - padding;

      // Only attempt to add image if we have at least 0.5 inches of space
      if (remainingHeight < 0.5) {
        console.warn(`Skipping image ${idx + 1}: insufficient space (${remainingHeight.toFixed(2)} inches remaining)`);
        return;
      }

      try {
        let imgWidth, imgHeight;

        // If we have natural dimensions, use them to maintain aspect ratio
        if (image.naturalWidth && image.naturalHeight) {
          const aspectRatio = image.naturalHeight / image.naturalWidth;

          // Convert pixels to inches (rough conversion: 96 DPI)
          const naturalWidthInches = image.naturalWidth / 96;
          const naturalHeightInches = image.naturalHeight / 96;

          // Start with natural size
          imgWidth = naturalWidthInches;
          imgHeight = naturalHeightInches;

          // Scale down if too wide
          if (imgWidth > contentWidth) {
            imgWidth = contentWidth;
            imgHeight = imgWidth * aspectRatio;
          }

          // Scale down if too tall - be conservative (leave 0.1 inch buffer)
          const maxHeight = remainingHeight - 0.1;
          if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight / aspectRatio;
          }
        } else {
          // Fallback: use available space if no natural dimensions
          imgWidth = Math.min(contentWidth, 2.0);
          imgHeight = Math.min(remainingHeight - 0.1, 1.5);
        }

        // Ensure minimum viable size
        if (imgWidth < 0.5) imgWidth = 0.5;
        if (imgHeight < 0.3) imgHeight = 0.3;

        // Final check: ensure image fits in remaining space
        if (imgHeight > remainingHeight - 0.05) {
          console.warn(`Skipping image ${idx + 1}: scaled height (${imgHeight.toFixed(2)}) exceeds remaining space (${remainingHeight.toFixed(2)})`);
          return;
        }

        slide.addImage({
          data: image.data,
          x: x + padding,
          y: currentY,
          w: imgWidth,
          h: imgHeight,
          sizing: { type: 'contain', w: imgWidth, h: imgHeight }
        });
        currentY += imgHeight + 0.1;
      } catch (error) {
        console.error('Error adding image:', error);
      }
    });
  };

  const leftHeaderX = leftMargin;
  const leftHeaderY = topMargin;

  // Left header box
  const leftHeaderText = [
    { text: isKnowledgeGap ? 'Knowledge Gap: ' : 'Key Decision: ', options: { bold: true } },
    { text: (formData.title || 'Untitled') + '\n' },
    { text: 'Owner: ', options: { bold: true } },
    { text: (formData.owner || '') + '\n' },
    { text: isKnowledgeGap ? 'Contributors: ' : 'Decision Maker: ', options: { bold: true } },
    { text: (isKnowledgeGap ? (formData.contributors || '') : (formData.decision_maker || '')) + '\n' },
    { text: isKnowledgeGap ? 'Learning Cycle: ' : 'Integration Event: ', options: { bold: true } },
    { text: formData.learning_cycle || '' }
  ];

  slide.addText(leftHeaderText, {
    x: leftHeaderX, y: leftHeaderY, w: leftBoxWidth, h: headerHeight,
    fontSize: 10, fontFace: 'Arial',
    fill: { color: headerBg },
    line: { color: borderColor, width: 1 },
    shape: pptx.ShapeType.roundRect,
    rectRadius: cornerRadius,
    valign: 'top',
    margin: [headerTextPadding, headerTextPadding, headerTextPadding, headerTextPadding]
  });

  // Right header box
  const rightHeaderX = leftHeaderX + leftBoxWidth + gap;

  const rightHeaderText = [
    { text: (formData.project_name || '') + '\n', options: { bold: true } },
    { text: (isKnowledgeGap ? `Knowledge Gap #${formData.kg_number || '______'}` : `Key Decision #${formData.kd_number || '______'}`) + '\n', options: { bold: true } },
    { text: 'Status: ', options: { bold: true } },
    { text: formData.status || 'draft' }
  ];

  slide.addText(rightHeaderText, {
    x: rightHeaderX, y: leftHeaderY, w: rightBoxWidth, h: headerHeight,
    fontSize: 10, fontFace: 'Arial',
    fill: { color: headerBg },
    line: { color: borderColor, width: 1 },
    shape: pptx.ShapeType.roundRect,
    rectRadius: cornerRadius,
    align: 'right',
    valign: 'top',
    margin: [headerTextPadding, headerTextPadding, headerTextPadding, headerTextPadding]
  });

  // Content boxes positions
  const contentStartY = leftHeaderY + headerHeight + 0.15;

  // Left column - Box 1: The Question/Decision
  const box1Y = contentStartY;
  const box1H = 0.9;

  // Left column - Box 2: The Purpose
  const box2Y = box1Y + box1H + 0.12;
  const box2H = 1.25;

  // Left column - Box 3: What We Have Done
  const box3Y = box2Y + box2H + 0.12;
  const box3H = slideHeight - box3Y - topMargin;

  // Right column - Box 1: What We Have Learned
  const rightBox1Y = contentStartY;
  const rightBox1H = isKnowledgeGap ? 3.4 : 2.75;

  // Right column - Box 2: Recommendations
  const rightBox2Y = rightBox1Y + rightBox1H + 0.12;
  const rightBox2H = slideHeight - rightBox2Y - topMargin;

  // Add all content boxes using the new helper (with tables, images, hyperlinks)
  await addContentToBox(
    formData.description,
    leftHeaderX,
    box1Y,
    leftBoxWidth,
    box1H,
    isKnowledgeGap ? 'The Question to Answer: ' : 'The Key Decision: '
  );

  await addContentToBox(
    formData.purpose,
    leftHeaderX,
    box2Y,
    leftBoxWidth,
    box2H,
    'The Purpose: '
  );

  await addContentToBox(
    formData.what_we_have_done,
    leftHeaderX,
    box3Y,
    leftBoxWidth,
    box3H,
    isKnowledgeGap ? 'What We Will Do / What We Have Done: ' : 'What We Have Done: '
  );

  await addContentToBox(
    formData.what_we_have_learned,
    rightHeaderX,
    rightBox1Y,
    rightBoxWidth,
    rightBox1H,
    'What We Have Learned: '
  );

  await addContentToBox(
    formData.recommendations,
    rightHeaderX,
    rightBox2Y,
    rightBoxWidth,
    rightBox2H,
    isKnowledgeGap ? 'Recommendations and Next Steps: ' : 'What We Recommend / What We Have Decided: '
  );

  // Generate as Blob
  const pptxBlob = await pptx.write({ outputType: 'blob' });
  return pptxBlob;
};

/**
 * Generate filename for PowerPoint/PDF export
 *
 * @param {Object} formData - Report form data
 * @param {string} selectedReport - Report type: 'knowledge_gap' or 'key_decision'
 * @param {string} slideSize - Slide size: '16:9' or '4:3'
 * @param {string} extension - File extension: 'pptx' or 'pdf'
 * @returns {string} - Filename (e.g., "KG 123 Report Title Slide 16x9.pdf")
 */
export const generateExportFilename = (formData, selectedReport, slideSize, extension = 'pptx') => {
  const isKnowledgeGap = selectedReport === 'knowledge_gap';
  const reportType = isKnowledgeGap ? 'KG' : 'KD';
  const reportNumber = isKnowledgeGap ? (formData.kg_number || '') : (formData.kd_number || '');
  const slideSizeLabel = slideSize.replace(':', 'x');
  const filename = `${reportType} ${reportNumber} ${formData.title || 'Report'} Slide ${slideSizeLabel}.${extension}`;
  return filename;
};

export default {
  generatePowerPointBlob,
  generateExportFilename
};
